import type { CSSProperties, ReactNode } from 'react';
import { NO_VALUE } from './format.ts';
import { Unit } from './Unit.tsx';
import styles from './KeyValue.module.css';

/* ==========================================================================
   KeyValue — the definition list for spec data.

   `grid`  label-above-value pairs, two (or n) columns — a detail rail.
   `rows`  label left / value right on a 20px pitch — a properties list.

   Values default to mono, because nearly everything in this product's spec
   data is a machine string. Value and unit are ONE cell: number in --text-hi,
   unit in --text-low at the same size, so numerals still align.
   ========================================================================== */

export interface KVItem {
  label: string;
  value: ReactNode;
  /** Dimmer suffix inside the value cell — `81.5 GiB`, `218 °`. */
  unit?: string | undefined;
  mono?: boolean | undefined;
  title?: string | undefined;
}

export function KeyValue({
  items,
  layout = 'grid',
  cols = 2,
  className,
}: {
  items: readonly KVItem[];
  layout?: 'grid' | 'rows' | undefined;
  cols?: number | undefined;
  className?: string | undefined;
}) {
  const style = { '--kv-cols': cols } as CSSProperties;
  return (
    <dl
      className={className === undefined ? styles.kv : `${styles.kv} ${className}`}
      data-layout={layout}
      style={style}
    >
      {items.map((it) =>
        layout === 'grid' ? (
          <div className={styles.pair} key={it.label}>
            <dt className={styles.dt}>{it.label}</dt>
            <dd className={styles.dd} data-mono={it.mono !== false} title={it.title}>
              <Value value={it.value} unit={it.unit} />
            </dd>
          </div>
        ) : (
          <Row key={it.label} item={it} />
        ),
      )}
    </dl>
  );
}

function Row({ item }: { item: KVItem }) {
  return (
    <>
      <dt className={styles.dt}>{item.label}</dt>
      <dd className={styles.dd} data-mono={item.mono !== false} title={item.title}>
        <Value value={item.value} unit={item.unit} />
      </dd>
    </>
  );
}

function Value({ value, unit }: { value: ReactNode; unit?: string | undefined }) {
  const missing = value === null || value === undefined || value === '' || value === NO_VALUE;
  if (missing) return <span className={styles.missing}>{NO_VALUE}</span>;
  return (
    <>
      {value}
      {unit !== undefined && (
        <Unit unit={unit} />
      )}
    </>
  );
}
